/* Note naming (Do-Re-Mi and C-D-E), parsing, intervals and scales. */
import { getSettings, type NoteNaming } from '../store/state';

export const mod = (n: number, m: number): number => ((n % m) + m) % m;

export const PC_SHARP = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
export const PC_FLAT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const;
export const SOL_SHARP = ['Do', 'Do#', 'Re', 'Re#', 'Mi', 'Fa', 'Fa#', 'Sol', 'Sol#', 'La', 'La#', 'Si'] as const;
export const SOL_FLAT = ['Do', 'Reb', 'Re', 'Mib', 'Mi', 'Fa', 'Solb', 'Sol', 'Lab', 'La', 'Sib', 'Si'] as const;
export const NATURALS: readonly number[] = [0, 2, 4, 5, 7, 9, 11];
export const LETTER_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Display form: "#" -> "♯", "b" -> "♭", "7b5" -> "7♭5". */
export function pretty(s: string): string {
  return String(s)
    .replace(/7b5/g, '7♭5')
    .replace(/#/g, '♯')
    .replace(/(^|[^a-z])(Do|Re|Mi|Fa|Sol|La|Si|[A-G])b/g, '$1$2♭');
}

/** "C", "F#", "Bb" -> pitch class. */
export function parsePc(s: string): number | null {
  const m = /^([A-G])(#|b)?$/.exec(String(s).trim());
  if (!m) return null;
  return mod((LETTER_PC[m[1] as string] as number) + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0), 12);
}

/** "C4", "F#3", "Bb2" -> MIDI note. */
export function parseNote(s: string): number | null {
  const m = /^([A-G])(#|b)?(-?\d)$/.exec(String(s).trim());
  if (!m) return null;
  return (
    (Number.parseInt(m[3] as string, 10) + 1) * 12 +
    (LETTER_PC[m[1] as string] as number) +
    (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0)
  );
}

export interface NameOptions {
  /** Force flat (true) or sharp (false) spelling; defaults to the user's setting. */
  flat?: boolean;
  /** Override the user's naming system. */
  style?: NoteNaming;
  /** noteParts only: include the octave number (default true). */
  octave?: boolean;
}

const flatOf = (o?: NameOptions): boolean => (o?.flat != null ? o.flat : getSettings().accidental === 'flat');
const styleOf = (o?: NameOptions): NoteNaming => o?.style || getSettings().noteNames;

/** A note with an optional offset in cents: "E4" -> 64, "E4-14c" -> 63.86, "B3+50c" -> 59.5. */
export function parsePitch(s: string): number | null {
  const m = /^([A-G](?:#|b)?-?\d)(?:([+-]\d{1,2}(?:\.\d+)?)c)?$/.exec(String(s).trim());
  if (!m) return null;
  const midi = parseNote(m[1] as string);
  return midi == null ? null : midi + (m[2] ? Number.parseFloat(m[2]) / 100 : 0);
}

export const letter = (pc: number, flat?: boolean): string => (flat ? PC_FLAT : PC_SHARP)[mod(pc, 12)] as string;
export const solfege = (pc: number, flat?: boolean): string => (flat ? SOL_FLAT : SOL_SHARP)[mod(pc, 12)] as string;
export const octave = (midi: number): number => Math.floor(midi / 12) - 1;

/** Pitch-class name in the user's naming system: "Fa♯ (F♯)", "Fa♯" or "F♯". */
export function pcName(pc: number, o: NameOptions = {}): string {
  const st = styleOf(o);
  const flat = flatOf(o);
  const L = pretty(letter(pc, flat));
  const S = pretty(solfege(pc, flat));
  if (st === 'letter') return L;
  if (st === 'solfege') return S;
  return `${S} (${L})`;
}

/** Specific note: "Sol3 (G3)", "Sol3" or "G3". */
export function noteName(midi: number, o: NameOptions = {}): string {
  const st = styleOf(o);
  const flat = flatOf(o);
  const pc = mod(midi, 12);
  const oc = octave(midi);
  const L = pretty(letter(pc, flat)) + oc;
  const S = pretty(solfege(pc, flat)) + oc;
  if (st === 'letter') return L;
  if (st === 'solfege') return S;
  return `${S} (${L})`;
}

export interface NameParts {
  main: string;
  sub: string;
}

/** Two-part label for compact UI. */
export function noteParts(midi: number, o: NameOptions = {}): NameParts {
  const st = styleOf(o);
  const flat = flatOf(o);
  const pc = mod(midi, 12);
  const oc = octave(midi);
  const L = pretty(letter(pc, flat));
  const S = pretty(solfege(pc, flat));
  const withOct = o.octave !== false;
  if (st === 'letter') return { main: L + (withOct ? oc : ''), sub: '' };
  if (st === 'solfege') return { main: S + (withOct ? oc : ''), sub: '' };
  return { main: S + (withOct ? oc : ''), sub: L + (withOct ? oc : '') };
}

export function pcParts(pc: number, o: NameOptions = {}): NameParts {
  const st = styleOf(o);
  const flat = flatOf(o);
  const L = pretty(letter(pc, flat));
  const S = pretty(solfege(pc, flat));
  if (st === 'letter') return { main: L, sub: '' };
  if (st === 'solfege') return { main: S, sub: '' };
  return { main: S, sub: L };
}

/** Short pitch-class label for dense UI (fretboard dots): solfege unless the user picked letters. */
export function shortPc(pc: number, flat?: boolean): string {
  const st = getSettings().noteNames;
  return pcParts(pc, { flat, style: st === 'letter' ? 'letter' : 'solfege' }).main;
}

export const shortNote = (midi: number, o?: NameOptions): string => noteParts(midi, o).main;

export const midiToFreq = (m: number): number => (getSettings().a4 || 440) * 2 ** ((m - 69) / 12);
export const freqToMidi = (f: number): number => 69 + 12 * Math.log2(f / (getSettings().a4 || 440));

// ------------------------------------------------------------ intervals
export const INTERVALS = [
  { st: 0, name: 'Aynı nota', short: '1' },
  { st: 1, name: "Küçük 2'li", short: 'k2' },
  { st: 2, name: "Büyük 2'li", short: 'B2' },
  { st: 3, name: "Küçük 3'lü", short: 'k3' },
  { st: 4, name: "Büyük 3'lü", short: 'B3' },
  { st: 5, name: "Tam 4'lü", short: 'T4' },
  { st: 6, name: 'Triton', short: 'TT' },
  { st: 7, name: "Tam 5'li", short: 'T5' },
  { st: 8, name: "Küçük 6'lı", short: 'k6' },
  { st: 9, name: "Büyük 6'lı", short: 'B6' },
  { st: 10, name: "Küçük 7'li", short: 'k7' },
  { st: 11, name: "Büyük 7'li", short: 'B7' },
  { st: 12, name: 'Oktav', short: '8' },
] as const;

export const intervalName = (st: number): string => INTERVALS[Math.abs(st)]?.name ?? `${Math.abs(st)} yarım ses`;

// ------------------------------------------------------------ scales
export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  harmMinor: [0, 2, 3, 5, 7, 8, 11],
  majPent: [0, 2, 4, 7, 9],
  minPent: [0, 3, 5, 7, 10],
  blues: [0, 3, 5, 6, 7, 10],
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
} as const satisfies Record<string, readonly number[]>;
export type ScaleName = keyof typeof SCALES;

/** All MIDI notes of a scale between lo..hi. */
export function scaleNotes(tonicPc: number, scale: ScaleName, lo: number, hi: number): number[] {
  const set = new Set(SCALES[scale].map((i) => mod(tonicPc + i, 12)));
  const out: number[] = [];
  for (let m = lo; m <= hi; m++) if (set.has(mod(m, 12))) out.push(m);
  return out;
}
